import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import get from 'lodash/get';
import Button from '../../core/components/Button';

import * as Config from '../../../../config.json';
import styles from '../../../css/paymentPage/subComponents/SquarePaymentComponent.scss';

const pageStyles = typeof styles === 'function' ? styles() : styles;

class SquarePaymentComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      cardButtonDisabled: false,
      squareCard: null,
    };
  }

  componentDidMount = async () => {
    // eslint-disable-next-line no-undef
    if (!window.Square) {
      throw new Error('Square.js failed to load properly');
    }
    let card;
    try {
      card = await this.initializeCard();
    } catch (e) {
      console.error('Initializing Card failed', e);
      return;
    }
    this.setState({ squareCard: card });
  }

  getCardOptions = () => {
    // Need to use Square's class selectors with the dots in front. Otherwise, the form crashes
    const cardClassSelectors = {
      input: pageStyles.input,
      'input.is-error': pageStyles['input.is-error'],
      'input.is-error::placeholder': pageStyles['input.is-error-placeholder'],
      'input.is-focus': pageStyles['input.is-focus'],
      'input.is-focus::placeholder': pageStyles['input.is-focus-placeholder'],
      'input::placeholder': pageStyles['input-placeholder'],
      '.message-text': pageStyles['message-text'],
      '.message-text.is-error': pageStyles['message-text.is-error'],
      '.message-icon': pageStyles['message-icon'],
      '.message-icon.is-error': pageStyles['message-icon.is-error'],
      '.input-container': pageStyles['input-container'],
      '.input-container.is-error': pageStyles['input-container.is-error'],
      '.input-container.is-focus': pageStyles['input-container.is-focus'],
    };
    return ({ style: cardClassSelectors });
  }

  initializeCard = async () => {
    const { currentOrder } = this.props;
    const appId = Config.general.squareAppId;
    const locationId = get(currentOrder, 'location.squareId');
    // eslint-disable-next-line no-undef
    const payments = window.Square.payments(appId, locationId);
    const cardOptions = this.getCardOptions();
    const card = await payments.card(cardOptions);
    await card.attach('#card-container');
    return card;
  }

  resetCardForm = async () => {
    const { squareCard } = this.state;
    await squareCard.destroy();
    let card;
    try {
      card = await this.initializeCard();
    } catch (e) {
      console.error('Initializing Card failed', e);
      return;
    }
    this.setState({ squareCard: card });
  }

  handlePaymentMethodSubmission = async (paymentMethod) => {
    try {
      // disable the submit button as we await tokenization and make a
      // payment request.
      this.setState({ cardButtonDisabled: true });
      const result = await this.tokenize(paymentMethod);
      const addCardResult = await this.addCard(result);
      this.displayPaymentResults('SUCCESS');
      console.debug('Payment Success', addCardResult);
    } catch (e) {
      this.setState({ cardButtonDisabled: false });
      this.displayPaymentResults('FAILURE');
      console.error(e.message);
    }
  }

  addCard = async (tokenizeResult) => {
    const { user, actions, handleAddCard } = this.props;
    const card = tokenizeResult.details;
    const cardToken = tokenizeResult.token;

    const paymentMethod = {
      name: user.name,
      cardNumber: cardToken,
      expiryMonth: card.expMonth,
      expiryYear: card.expYear,
    };

    const newCardResponse = await actions
      .addResource(
        user.token, paymentMethod,
        'users', user.id,
        'payment_options',
      );
    this.resetCardForm();
    this.setState({ cardButtonDisabled: false });
    if (newCardResponse.id) {
      handleAddCard(newCardResponse);
    }
    if (!newCardResponse || newCardResponse.error) {
      console.log('There was an error', newCardResponse.error);
    }
  }

  // Call this function to send a payment token, buyer name, and other details
  // to the project server code so that a payment can be created with
  // Payments API
  // We can later use this to enable Google Pay / Apple Pay on web
  // CURRENTLY NOT IN USE
  // processPayment = async (token) => {
  //   const { locationId } = this.state;
  //   const body = JSON.stringify({
  //     locationId,
  //     sourceId: token,
  //   });
  //   const paymentResponse = await fetch('/payment', {
  //     method: 'POST',
  //     headers: {
  //       'Content-Type': 'application/json',
  //     },
  //     body,
  //   });
  //   if (paymentResponse.ok) {
  //     return paymentResponse.json();
  //   }
  //   const errorBody = await paymentResponse.text();
  //   throw new Error(errorBody);
  // }

  // This function tokenizes a payment method.
  // The ‘error’ thrown from this async function denotes a failed tokenization,
  // which is due to buyer error (such as an expired card). It is up to the
  // developer to handle the error and provide the buyer the chance to fix
  // their mistakes.
  tokenize = async (paymentMethod) => {
    const tokenResult = await paymentMethod.tokenize();
    if (tokenResult.status === 'OK') {
      return tokenResult;
    }
    let errorMessage = `Tokenization failed-status: ${tokenResult.status}`;
    if (tokenResult.errors) {
      errorMessage += ` and errors: ${JSON.stringify(tokenResult.errors)}`;
    }
    throw new Error(errorMessage);
  }

  // Helper method for displaying the Payment Status on the screen.
  // status is either SUCCESS or FAILURE;
  displayPaymentResults = (status) => {
    // eslint-disable-next-line no-undef
    const statusContainer = document.getElementById('payment-status-container');
    if (status === 'SUCCESS') {
      statusContainer.classList.remove('is-failure');
      statusContainer.classList.add('is-success');
    } else {
      statusContainer.classList.remove('is-success');
      statusContainer.classList.add('is-failure');
    }

    statusContainer.style.visibility = 'visible';
  };


  render() {
    const { cardButtonDisabled, squareCard } = this.state;
    const { translation } = this.props;

    return (
      <div>
        <form id="payment-form">
          <div id="card-container" />
          <Button
            disabled={cardButtonDisabled}
            onClick={() => this.handlePaymentMethodSubmission(squareCard)}
            id="card-button"
            text={translation('SquarePaymentComponent.addCardButton')}
            type="primary"
          />
        </form>
        <div id="payment-status-container" />
      </div>
    );
  }
}

SquarePaymentComponent.propTypes = {
  translation: PropTypes.func.isRequired,
  user: PropTypes.objectOf(PropTypes.any).isRequired,
  actions: PropTypes.objectOf(PropTypes.func).isRequired,
  currentOrder: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default withStyles(styles)(SquarePaymentComponent);
